import React from 'react';

import { FullscreenLoader } from '@hrlogics/react-components';

import {
  PeoStaffListDocument,
  PeoStaffRole,
  useSendInvitesToPeoStaffByPeoAdminMutation,
} from 'src/api';
import { PEO_ROLE_OPTIONS } from 'src/constants/peo';
import { useNotification } from 'src/hooks/app';
import { useCurrentUserPeo } from 'src/hooks/peo';
import { useCurrentUserPermissions } from 'src/hooks/user';

import { InvitePeoStaffForm } from './InvitePeoStaffForm';
import type { InviteFormFieldsValues } from './types';
import { convertStaff } from './utils/convertStaff';

interface InvitePeoStaffByPeoAdminModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InvitePeoStaffByPeoAdminModalForm: React.FC<
  InvitePeoStaffByPeoAdminModalFormProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const { peo } = useCurrentUserPeo();
  const { isPeoAdmin } = useCurrentUserPermissions();

  const [sendInvites] = useSendInvitesToPeoStaffByPeoAdminMutation({
    refetchQueries: [PeoStaffListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const handleSubmit = React.useCallback(
    async (formFields: InviteFormFieldsValues) => {
      switchClosability(false);

      try {
        await sendInvites({
          variables: {
            staff: convertStaff(formFields.staff),
          },
        });

        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [switchClosability, sendInvites, closeModal],
  );

  const roleFieldSelectOptions = React.useMemo(() => {
    return PEO_ROLE_OPTIONS.filter(option => {
      if (isPeoAdmin) {
        return option.value !== PeoStaffRole.PeoSuperAdmin;
      }
      return true;
    });
  }, [isPeoAdmin]);

  if (!peo?.id) {
    return <FullscreenLoader />;
  }

  return (
    <InvitePeoStaffForm
      peoId={peo.id}
      roleFieldSelectOptions={roleFieldSelectOptions}
      onClose={closeModal}
      onSubmit={handleSubmit}
    />
  );
};
